import { Combobox } from "@headlessui/react";
import { ReactNode, useEffect, useState } from "react";
import { Icon, Typography } from "@/components/atoms";
import type { Place } from "@/hooks/usePlaces";
import { PointsOfInterest, RM_TAGS } from "@/redux/apis/clientPoi/types";
import { clsx } from "@/utils";
import { LatLng } from "../suggestions";
import { useAddressSearch } from "./useAddressSearch";

interface LocationAddressAutocompleteProps {
  value: string;
  onChange: (text: string) => void;
  onSelect: (poi: PointsOfInterest) => void;
  // Optional proximity-ranking hint (reserved for parity with mobile's distance boost).
  // Not yet supplied by callers; rankLocalPois falls back to text-only ranking when absent.
  userLocation?: LatLng | null;
  // When true, includes saved client favourites (Home/Work/Other) in suggestions.
  // Default false — the add-favourite flow excludes them to avoid self-referencing.
  includeFavourites?: boolean;
  /** Countries results may come from (lower-case ISO codes); defaults to the provider's bookable list; `null` = worldwide. See useAddressSearch. */
  countries?: string[] | null;
  placeholder?: string;
  renderIcon?: () => JSX.Element;
  className?: string;
  // Fired when the input gains focus — lets the booking sheet mark which field is active
  // (so the map pin picker / suggestions know where to write).
  onFocusField?: () => void;
  // Right-aligned element inside the input (e.g. a clear ✕ while typing, or a favourite ☆ once set).
  trailing?: ReactNode;
  // Primary-coloured helper text shown beneath the input while focused (e.g. "Enter street address only…").
  infoMessage?: string;
}

type AddressSelection = { kind: "poi"; poi: PointsOfInterest } | { kind: "google"; place: Place };

const AMBER = "text-[#F79009]";
const BLACK = "text-neutral-black";

/** Title for a local POI row — its label, falling back to the first address segment (mobile parity:
 * recents often have an empty rm_label). */
const poiTitle = (poi: PointsOfInterest): string => poi.rm_label?.trim() || poi.address?.split(",")[0]?.trim() || poi.address || "";

/** Leading icon for a suggestion. Custom/local POIs use an amber tag icon by rm_tags; recents,
 * untagged items, and Google results all use the same black outline icon (mobile parity + web Figma). */
export const suggestionIcon = (rmTags: string[] | undefined, isGoogle: boolean): { name: string; className: string } => {
  if (isGoogle) return { name: "Location", className: BLACK };
  const tags = rmTags ?? [];
  if (tags.includes(RM_TAGS.Airport)) return { name: "Airplane", className: AMBER };
  if (tags.includes(RM_TAGS.Home)) return { name: "Home", className: AMBER };
  if (tags.includes(RM_TAGS.Work)) return { name: "Briefcase", className: AMBER };
  if (tags.includes(RM_TAGS.Other)) return { name: "Star1", className: AMBER };
  if (tags.includes(RM_TAGS.Recent)) return { name: "Clock", className: BLACK };
  if (tags.length > 0) return { name: "Location", className: AMBER }; // major_poi / hotel / other known POI
  return { name: "Clock", className: BLACK }; // untagged local item = a recent
};

/** One suggestion row, styled to match the mobile favourites picker. Custom/local POIs use an amber
 * tag icon; Google results use a black outline location pin. */
const AddressOptionRow = ({
  iconName,
  iconClassName,
  main,
  secondary,
}: {
  iconName: string;
  iconClassName: string;
  main: string;
  secondary: string;
}) => (
  <div className="flex w-full items-center gap-3 px-3 py-2.5">
    <Icon name={iconName} variant="Linear" size="md" className={clsx("shrink-0", iconClassName)} />
    <div className="flex min-w-0 flex-1 flex-col">
      <Typography className="truncate font-semibold text-neutral-black">{main}</Typography>
      <Typography variant="small" className="truncate text-neutral-silver-gray">
        {secondary}
      </Typography>
    </div>
  </div>
);

export const LocationAddressAutocomplete = ({
  value,
  onChange,
  onSelect,
  userLocation,
  includeFavourites = false,
  countries,
  placeholder = "Search or type an address",
  renderIcon,
  className,
  onFocusField,
  trailing,
  infoMessage,
}: LocationAddressAutocompleteProps) => {
  const [query, setQuery] = useState(value);
  const [isFocused, setIsFocused] = useState(false);

  // Keep internal query in sync with the controlled `value` prop without clobbering the caret.
  useEffect(() => {
    if (value !== query) setQuery(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const { localMatches, remoteMatches, resolvePlace } = useAddressSearch({
    query,
    enabled: isFocused,
    includeFavourites,
    userLocation,
    countries,
  });

  const handleChange = (text: string) => {
    setQuery(text);
    onChange(text);
  };

  const handleSelect = (selected: AddressSelection | null) => {
    if (!selected) return;
    if (selected.kind === "poi") {
      onSelect(selected.poi);
      return;
    }
    resolvePlace(selected.place).then((poi) => {
      if (poi) onSelect(poi);
    });
  };

  const hasOptions = localMatches.length > 0 || remoteMatches.length > 0;

  return (
    <Combobox<AddressSelection | null, false, "div"> as="div" value={null} onChange={handleSelect} nullable>
      <div className={clsx("relative w-full", className)}>
        <div className="relative">
          <Combobox.Input
            className={clsx(
              "h-12 w-full rounded-lg border-neutral-mid-gray pl-10 text-base text-neutral-black placeholder-neutral-silver-gray focus:border-primary focus:shadow-input focus:ring-0",
              trailing ? "pr-12" : ""
            )}
            displayValue={() => query}
            onChange={(e) => handleChange(e.target.value)}
            onFocus={() => {
              setIsFocused(true);
              onFocusField?.();
            }}
            // Delay so option clicks (which blur the input first) still register before focus clears.
            onBlur={() => setTimeout(() => setIsFocused(false), 150)}
            placeholder={placeholder}
            autoComplete="off"
          />
          <span className="absolute inset-y-0 left-0 flex items-center px-3">
            {renderIcon ? renderIcon() : <Icon name="SearchNormal1" size="sm" className="text-neutral-silver-gray" />}
          </span>
          {trailing && <span className="absolute inset-y-0 right-0 flex items-center pr-3">{trailing}</span>}
        </div>
        {infoMessage && isFocused && (
          <Typography variant="small-regular" className="mt-1 block text-primary">
            {infoMessage}
          </Typography>
        )}

        {hasOptions && (
          <Combobox.Options className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-neutral-mid-gray bg-white shadow-lg focus:outline-none">
            {localMatches.map((poi) => {
              const icon = suggestionIcon(poi.rm_tags, false);
              return (
                <Combobox.Option
                  key={`local-${poi.uuid}`}
                  value={{ kind: "poi", poi } satisfies AddressSelection}
                  className="cursor-pointer hover:bg-neutral-surface-gray data-[active]:bg-neutral-surface-gray"
                >
                  <AddressOptionRow iconName={icon.name} iconClassName={icon.className} main={poiTitle(poi)} secondary={poi.address} />
                </Combobox.Option>
              );
            })}
            {remoteMatches.map((place) => {
              const icon = suggestionIcon(undefined, true);
              return (
                <Combobox.Option
                  key={`google-${place.place_id}`}
                  value={{ kind: "google", place } satisfies AddressSelection}
                  className="cursor-pointer hover:bg-neutral-surface-gray data-[active]:bg-neutral-surface-gray"
                >
                  <AddressOptionRow
                    iconName={icon.name}
                    iconClassName={icon.className}
                    main={place.structured_formatting?.main_text ?? place.description}
                    secondary={place.description}
                  />
                </Combobox.Option>
              );
            })}
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  );
};
